import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";
import { watchPreferredMotion } from "/utils/helpers/watchPreferredMotion";
import DefaultBlock from "/scripts/default/block";
import { ScrambleTextPlugin } from "gsap/ScrambleTextPlugin";
import { SplitText } from "gsap/SplitText";
import { MorphSVGPlugin } from "gsap/MorphSVGPlugin";

gsap.registerPlugin(ScrollTrigger, ScrambleTextPlugin, MorphSVGPlugin);

export default class TextIntro extends DefaultBlock {
	init() {
		this.state = {};

		this.DOM = {
			main: this.block.querySelector("#text-intro"),
		};

		this.createAnimation();
	}

	createAnimation() {
		gsap.set(this.DOM.main, { autoAlpha: 1 });

		watchPreferredMotion(() => {
			gsap.set(this.DOM.main, { opacity: 0 });

			const createTl = () => {
				const DOM = {
					text: this.block.querySelector("#text-intro-create"),
					shapes: this.block.querySelectorAll("#create > g"),
					star: this.block.querySelector("#star"),
					bubble: this.block.querySelector("#bubble"),
				};
				const tl = gsap.timeline({
					id: "create",
					defaults: {
						duration: 0.15,
						ease: "power3.out",
					},
				});

				tl.set(DOM.shapes, { opacity: 0 });
				tl.from(DOM.text, {
					yPercent: 100,
					duration: 0.2,
				})
					.fromTo(
						DOM.star,
						{
							xPercent: -100,
							opacity: 1,
						},
						{
							xPercent: 0,
							opacity: 1,
						},
						">-0.1"
					)
					.fromTo(
						DOM.bubble,
						{
							yPercent: 100,
							opacity: 1,
						},
						{
							yPercent: 0,
							opacity: 1,
						}
					);

				return tl;
			};

			const complexTl = () => {
				const DOM = {
					diamond: this.block.querySelector("#text-intro-complex-svg"),
					diamondSvg: this.block.querySelector("#text-intro-complex-svg svg"),
					text: this.block.querySelector("#text-intro-complex-text"),
				};
				const tl = gsap.timeline({
					id: "complex",
					defaults: {
						duration: 0.15,
						ease: "power3.out",
					},
				});

				const diamondWidth = DOM.diamond.clientWidth;
				const diamondPadding = gsap.getProperty(DOM.diamond, "paddingLeft");
				const textWidth = DOM.text.clientWidth;

				tl.set(DOM.diamond, {
					x: -textWidth + diamondWidth - diamondPadding * 2,
				})
					.fromTo(
						DOM.diamondSvg,
						{ yPercent: -100 },
						{ yPercent: 0, duration: 0.25 }
					)
					.fromTo(
						DOM.text,
						{
							opacity: 0,
						},
						{
							opacity: 1,
							duration: 0.01,
						}
					)
					.fromTo(
						DOM.text,
						{
							clipPath: "polygon(0% 0%, 6.2% 0%, 6.2% 100%, 0% 100%)",
						},
						{
							clipPath: "polygon(0% 0%, 88.8% 0%, 88.8% 100%, 0% 100%)",
							duration: 0.3,
							ease: "power3.inOut",
						}
					)
					.to(
						DOM.diamond,
						{
							x: 0,
							duration: 0.3,
							ease: "power3.inOut",
						},
						"<"
					);

				return tl;
			};

			const textTl = () => {
				const DOM = {
					text: this.block.querySelector("#text-intro-text-text"),
					scramble: this.block.querySelector("#text-intro-text-scramble"),
					circleBottom: this.block.querySelector("#circle-bottom"),
					circleTop: this.block.querySelector("#circle-top"),
				};
				const tl = gsap.timeline({
					id: "text",
					defaults: {
						duration: 0.15,
						ease: "power3.out",
					},
				});

				gsap.set(DOM.text, { opacity: 0 });

				tl.fromTo(
					DOM.scramble,
					{ scrambleText: "" },
					{
						scrambleText: {
							text: "Text",
							chars: "lowercase",
							speed: 0.5,
						},
						duration: 0.6,
						ease: "none",
					}
				).from([DOM.circleBottom, DOM.circleTop], {
					xPercent: -100,
					stagger: 0.05,
				});

				return tl;
			};

			const effectsTl = () => {
				const DOM = {
					text: this.block.querySelector("#text-intro-effects-text"),
					tubeBtmLHide: this.block.querySelector("#tube_bottom_l_hide"),
					tubeTopRHide: this.block.querySelector("#tube_top_r_hide"),
					tubeTopR: this.block.querySelector("#tube_top_r"),
					tubeBtmR: this.block.querySelector("#tube_bottom_r"),
					tubeTopL: this.block.querySelector("#tube_top_l"),
					tubeBtmL: this.block.querySelector("#tube_bottom_l"),
				};
				const tl = gsap.timeline({
					id: "effects",
					defaults: {
						duration: 0.15,
						ease: "power3.out",
					},
				});
				const splitText = new SplitText(DOM.text, { type: "chars" });

				gsap.set([DOM.tubeBtmLHide, DOM.tubeTopRHide], { opacity: 0 });
				gsap.set([DOM.tubeTopR, DOM.tubeBtmR], {
					x: -126,
				});

				tl.from(splitText.chars, {
					yPercent: 100,
					stagger: 0.03,
				})
					.from([DOM.tubeTopR, DOM.tubeTopL], {
						yPercent: -100,
					})
					.from(
						[DOM.tubeBtmR, DOM.tubeBtmL],
						{
							yPercent: 100,
						},
						"<"
					)
					.to([DOM.tubeTopR, DOM.tubeBtmR], {
						x: 0,
						duration: 0.2,
						ease: "power3.inOut",
					})
					.to(
						DOM.tubeBtmL.querySelectorAll("path"),
						{
							morphSVG: DOM.tubeBtmLHide
								.querySelector("path")
								.getAttribute("d"),
							duration: 0.2,
							ease: "power3.inOut",
						},
						"<"
					)
					.to(DOM.tubeTopR.querySelectorAll("path"), {
						morphSVG: DOM.tubeTopRHide.querySelector("path").getAttribute("d"),
						duration: 0.2,
						ease: "power3.inOut",
					});

				return tl;
			};

			const sequenceTl = () => {
				const DOM = {
					andText: this.block.querySelector("#text-intro-sequence-a"),
					sequenceText: this.block.querySelector("#text-intro-sequence-b"),
					svg: this.block.querySelector("#sequence"),
				};
				const tl = gsap.timeline({
					id: "sequence",
					defaults: {
						duration: 0.15,
						ease: "power3.out",
					},
				});

				tl.from([DOM.andText, DOM.sequenceText], {
					yPercent: 100,
					stagger: 0.1,
					duration: 0.25,
				}).from(
					DOM.svg,
					{
						xPercent: -100,
					},
					">-0.1"
				);

				return tl;
			};

			const animsTl = () => {
				const DOM = {
					text: this.block.querySelector("#text-intro-anims-text"),
					scramble: this.block.querySelector("#text-intro-anims-scramble"),
					shapes: this.block.querySelectorAll("#animations > g"),
					star: this.block.querySelector("#animations #star"),
					circle: this.block.querySelector("#animations #circle"),
				};
				const tl = gsap.timeline({
					id: "animations",
					defaults: {
						duration: 0.15,
						ease: "power3.out",
					},
				});

				gsap.set(DOM.text, { opacity: 0 });
				gsap.set(DOM.shapes, { opacity: 0 });

				tl.fromTo(
					DOM.scramble,
					{ scrambleText: "" },
					{
						scrambleText: {
							text: "Animations",
							chars: "lowercase",
							speed: 0.5,
						},
						duration: 0.7,
						ease: "none",
					}
				)
					.fromTo(
						DOM.star,
						{
							yPercent: 100,
							opacity: 1,
						},
						{
							yPercent: 0,
							opacity: 1,
						}
					)
					.fromTo(
						DOM.circle,
						{
							yPercent: -100,
							opacity: 1,
						},
						{
							yPercent: 0,
							opacity: 1,
						},
						"<"
					);

				return tl;
			};

			const withTl = () => {
				const DOM = {
					text: this.block.querySelector("#text-intro-with-text"),
					svg: this.block.querySelector("#with"),
					circleA: this.block.querySelector("#with #circle_a"),
					circleB: this.block.querySelector("#with #circle_b"),
				};
				const tl = gsap.timeline({
					id: "with",
					defaults: {
						duration: 0.15,
						ease: "power3.out",
					},
				});
				const splitText = new SplitText(DOM.text, { type: "chars" });

				tl.from(splitText.chars, {
					yPercent: 100,
					stagger: 0.03,
				})
					.from(
						DOM.circleA,
						{
							scale: 0,
							transformOrigin: "50% 50%",
							duration: 0.25,
						},
						">-0.05"
					)
					.from(
						DOM.circleB,
						{
							scale: 0,
							transformOrigin: "50% 50%",
							duration: 0.25,
						},
						"<"
					)
					.to(
						DOM.svg,
						{
							rotate: 180,
							transformOrigin: "50% 50%",
							duration: 0.4,
						},
						"<0.1"
					);

				return tl;
			};

			const smileyTl = () => {
				const DOM = {
					text1: this.block.querySelector("#text-intro-smiley-text-1"),
					text2: this.block.querySelector("#text-intro-smiley-text-2"),
					text3: this.block.querySelector("#text-intro-smiley-text-3"),
					face: this.block.querySelector("#text-intro-face"),
					faceFront: this.block.querySelector("#face_front"),
				};
				const tl = gsap.timeline({
					id: "smiley",
					defaults: {
						duration: 0.15,
						ease: "power3.out",
					},
				});
				const splitText1 = new SplitText(DOM.text1, { type: "chars" });
				const splitText2 = new SplitText(DOM.text3, { type: "chars" });

				tl.from(splitText1.chars, {
					yPercent: 100,
					stagger: 0.03,
				})
					.from(DOM.text2, {
						yPercent: 100,
					})
					.set(DOM.faceFront, { rotateY: 180 })
					.from(DOM.face, {
						scale: 0,
						transformOrigin: "50% 50%",
					})
					.from(splitText2.chars, {
						yPercent: 100,
						stagger: 0.03,
					})
					.to(
						DOM.face,
						{
							rotateY: 180,
							transformOrigin: "50% 50%",
							ease: "back.out(2)",
							duration: 0.5,
						},
						"<0.1"
					);

				return tl;
			};

			const masterTl = gsap.timeline({
				id: "text-intro",
				scrollTrigger: {
					trigger: this.block,
					start: "top 60%",
				},
			});

			masterTl.timeScale(1.25);

			masterTl
				.set(this.DOM.main, { opacity: 1 }, 0)
				.add(createTl())
				.add(complexTl())
				.add(textTl())
				.add(effectsTl())
				.add(sequenceTl(), ">-0.2")
				.add(animsTl())
				.add(withTl())
				.add(smileyTl());
		});
	}
}
